import { Injectable, NgZone } from '@angular/core';
import { RdService } from './rd.service';
import { DeviceDetectorService } from './device-detector.service';
import { userParam, asQueryString } from '../models/user-params-models';
import { SURVEY_TRACKING_MESSAGES } from '../models/TrackUpdate';

/** Terminal outcomes stored per utype+tforid — blocks re-LANDING / re-DROPOUT on revisit. */
type TerminalKind = 'DROPOUT' | 'CONTINUE' | 'OPT_OUT' | 'REDIRECT';

/**
 * Landing-page temp-log tracker.
 * - markIntentionalExit() before Continue / Opt-out / client redirects
 * - beforeunload + visibilitychange + pagehide for abandon
 * - fetch + keepalive on exit
 * - After DROPOUT / CONTINUE / OPT_OUT for a utype+tforid+qparm: skip Start + Dropout on revisit;
 *   Continue / Opt-out clicks still always POST.
 *   Different qparm → new key → Start can fire again.
 */
@Injectable({ providedIn: 'root' })
export class SurveyLandingTrackService {

  private hasSentFinalEvent = false;
  private isIntentionalExit = false;
  private listenersAttached = false;
  private suppressExitLogging = false;
  private inactivityTimer: any = null;
  private inactivityDropoutTimer: any = null;
  private inactivityTracked = false;
  private readonly INACTIVITY_TIMEOUT = 5 * 60 * 1000;

  /** True only after RdInfoSecurityCheck 200 and Continue is available. */
  private leavePromptActive = false;
  private dropoutCallbackSent = false;
  /** One flagged Dropout per hidden stretch. Reset when the tab is visible again. */
  private tabSwitchDropoutSent = false;
  /** Refresh fires hidden, then pagehide. Hold the tab-switch send so pagehide can cancel it. */
  private tabSwitchTimer: any = null;
  private nativeUnloadBound = false;
  /** Real click / tap / key — required before Chrome will show Leave site? */
  private hasEngaged = false;

  private readonly nativeBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.isLeavePromptActive() || !this.hasEngaged) {
      return;
    }
    // Dialog only — user may click Cancel. Dropout is sent on pagehide after they actually leave.
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  private getUserParam: () => userParam | null = () => null;

  constructor(
    private rdService: RdService,
    private deviceDetector: DeviceDetectorService,
    private ngZone: NgZone
  ) {}

  /** Call once IP + query params are ready on survey-start. */
  startTracking(getUserParamFn: () => userParam): void {
    this.getUserParam = getUserParamFn;
    this.hasSentFinalEvent = false;
    this.isIntentionalExit = false;
    this.suppressExitLogging = false;
    this.inactivityTracked = false;
    this.leavePromptActive = false;
    this.dropoutCallbackSent = false;
    this.tabSwitchDropoutSent = false;
    this.clearTabSwitchTimer();
    this.hasEngaged = false;
    this.clearDropoutInactivityTimer();

    // Prior visit already terminal for this panel/survey → don't treat this open as a fresh abandon session
    if (this.hasTerminalStatus()) {
      this.hasSentFinalEvent = true;
    }

    this.attachExitListeners();
    this.ensurePhoneDropoutListeners();
    this.resetInactivityTimer();
    if (!this.hasTerminalStatus()) {
      this.enableLeavePrompt();
    }
  }

  /** Call before any programmatic leave (Continue redirect, Opt-out, RD auto-redirect). */
  markIntentionalExit(): void {
    this.isIntentionalExit = true;
    this.clearTabSwitchTimer();
    this.disableLeavePrompt();
    this.clearInactivityTimer();
    this.clearDropoutInactivityTimer();
  }

  /**
   * Native "Leave site?" on close / refresh. Chrome shows it only after a click on this page,
   * and never on tab switch.
   */
  enableLeavePrompt(): void {
    if (this.isIntentionalExit) {
      return;
    }
    this.leavePromptActive = true;
    if (this.hasEngaged) {
      this.bindNativeUnload();
    }
    this.resetDropoutInactivityTimer();
    this.ensurePhoneDropoutListeners();
  }

  disableLeavePrompt(): void {
    this.leavePromptActive = false;
    this.clearTabSwitchTimer();
    this.unbindNativeUnload();
    this.clearDropoutInactivityTimer();
  }

  isLeavePromptActive(): boolean {
    return this.leavePromptActive && !this.isIntentionalExit;
  }

  /** True after a real click / tap / key on this page. */
  hasUserEngagement(): boolean {
    return this.hasEngaged;
  }

  getDropoutDebug(): { count: number; lastText: string } {
    let count = 0;
    try {
      count = Number(localStorage.getItem('dropoutApiCount') || 0);
    } catch { }
    return { count, lastText: '' };
  }

  clearDropoutDebug(): void {
    this.dropoutCallbackSent = false;
    try {
      localStorage.removeItem('dropoutApiCount');
      localStorage.removeItem('dropoutApiResponse');
      localStorage.removeItem('dropoutApiLast');
    } catch { }
  }

  /** Keepalive POST to callbacks/Dropout. Safe to call more than once. */
  sendDropoutCallback(): void {
    this.clearTabSwitchTimer();
    if (!this.leavePromptActive || this.isIntentionalExit || this.dropoutCallbackSent || this.tabSwitchDropoutSent) {
      return;
    }
    this.dropoutCallbackSent = true;

    const user = this.getUserParam();
    if (!user?.ip && !user?.tforid) {
      return;
    }

    try {
      const payload: userParam = {
        ...user,
        qparm: asQueryString(user.qparm, ''),
        devicetype: this.deviceDetector.getDeviceType(),
        policy: user.policy ?? 0
      };
      const url = this.rdService.getDropoutCallbackUrl();
      const body = JSON.stringify(payload);
      this.postDropoutKeepalive(url, body);
    } catch { }
  }

  /** Hidden may be a tab switch, or the start of a refresh. Wait so pagehide can take the one real send. */
  private scheduleTabSwitchDropout(): void {
    if (this.tabSwitchTimer || this.tabSwitchDropoutSent || this.dropoutCallbackSent) {
      return;
    }
    if (this.shouldIgnoreBackgroundExit() || !this.isLeavePromptActive()) {
      return;
    }
    this.tabSwitchTimer = setTimeout(() => {
      this.tabSwitchTimer = null;
      if (document.visibilityState === 'visible' || this.dropoutCallbackSent) {
        return;
      }
      this.sendTabSwitchDropout();
    }, 50);
  }

  private clearTabSwitchTimer(): void {
    if (this.tabSwitchTimer) {
      clearTimeout(this.tabSwitchTimer);
      this.tabSwitchTimer = null;
    }
  }

  /** Same Dropout URL, only for a tab switch. A real leave in this same hide will not send again. */
  private sendTabSwitchDropout(): void {
    if (this.shouldIgnoreBackgroundExit() || !this.isLeavePromptActive() || this.tabSwitchDropoutSent || this.dropoutCallbackSent) {
      return;
    }
    const user = this.getUserParam();
    if (!user?.ip && !user?.tforid) {
      return;
    }
    this.tabSwitchDropoutSent = true;
    try {
      const payload: userParam = {
        ...user,
        qparm: asQueryString(user.qparm, ''),
        devicetype: this.deviceDetector.getDeviceType(),
        policy: user.policy ?? 0,
        isTabSwitch: true
      };
      this.postDropoutKeepalive(this.rdService.getDropoutCallbackUrl(), JSON.stringify(payload));
    } catch { }
  }

  /** Dropout POST while the page is open. Returns toid, or null if the API sent none. */
  async requestDropoutToid(): Promise<string | null> {
    const user = this.getUserParam();
    if (!user?.ip && !user?.tforid) {
      return null;
    }
    const payload: userParam = {
      ...user,
      qparm: asQueryString(user.qparm, ''),
      devicetype: this.deviceDetector.getDeviceType(),
      policy: user.policy ?? 0
    };
    const url = this.rdService.getDropoutCallbackUrl();
    const res = await fetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    this.bumpDropoutCount();
    const text = await res.text();
    if (!res.ok) {
      throw new Error('Dropout ' + res.status);
    }
    let toid: string | null = null;
    try {
      const json = JSON.parse(text);
      const value = json?.toid ?? json?.data?.toid ?? null;
      if (value != null && String(value).trim() !== '' && String(value) !== 'null') {
        toid = String(value);
      }
    } catch { }
    return toid;
  }

  /**
   * Soft status while user is still on the page.
   * LANDING / INACTIVE skipped if this utype+tforid already has a terminal status.
   * CONTINUE / OPT_OUT always sent and stored as terminal.
   */
  track(message: string): void {
    if (message === SURVEY_TRACKING_MESSAGES.LANDING_VIEW && this.hasTerminalStatus()) {
      return;
    }
    if (message === SURVEY_TRACKING_MESSAGES.INACTIVE && this.hasTerminalStatus()) {
      return;
    }

    this.sendTempLog(message, false);

    if (message === SURVEY_TRACKING_MESSAGES.CONTINUE_CLICKED) {
      this.setTerminalStatus('CONTINUE');
    } else if (message === SURVEY_TRACKING_MESSAGES.OPT_OUT) {
      this.setTerminalStatus('OPT_OUT');
    } else if (
      message === SURVEY_TRACKING_MESSAGES.REDIRECT_TO_CALLBACK ||
      message === SURVEY_TRACKING_MESSAGES.REDIRECT_TO_CLIENT ||
      message === SURVEY_TRACKING_MESSAGES.REDIRECT_TO_SCREENER ||
      message === SURVEY_TRACKING_MESSAGES.REDIRECT_TO_OLD_SCREENER
    ) {
      this.setTerminalStatus('REDIRECT');
    }
  }

  /** True when this utype+tforid already finished (dropout / continue / opt-out / redirect). */
  hasTerminalStatus(): boolean {
    return !!this.getTerminalStatus();
  }

  stopTracking(): void {
    this.clearInactivityTimer();
    this.clearDropoutInactivityTimer();
  }

  onUserActivity(): void {
    if (this.isIntentionalExit) {
      return;
    }
    this.resetDropoutInactivityTimer();
    if (this.hasSentFinalEvent || this.hasTerminalStatus()) {
      return;
    }
    this.resetInactivityTimer();
  }

  private attachExitListeners(): void {
    if (this.listenersAttached) {
      return;
    }
    this.listenersAttached = true;

    this.ngZone.runOutsideAngular(() => {
      const markEngaged = () => this.markEngaged();
      document.addEventListener('click', markEngaged, true);
      document.addEventListener('keydown', markEngaged, true);
      document.addEventListener('touchstart', markEngaged, true);

      const onTabVisibility = () => {
        if (document.visibilityState === 'visible') {
          this.clearTabSwitchTimer();
          this.tabSwitchDropoutSent = false;
          this.dropoutCallbackSent = false;
          return;
        }
        if (document.visibilityState === 'hidden' || document.hidden) {
          this.scheduleTabSwitchDropout();
        }
      };
      document.addEventListener('visibilitychange', onTabVisibility, true);
      window.addEventListener('visibilitychange', onTabVisibility, true);

      if (this.isPhoneOrTablet()) {
        this.bindPhoneDropoutListeners();
      } else {
        const onDesktopFreeze = () => this.markLeaveIfDropping();
        window.addEventListener('pagehide', (event: PageTransitionEvent) => {
          if (event.persisted) {
            return;
          }
          this.markLeaveIfDropping();
        });
        // Tab switch only hides the page. Freeze is later, when Chrome stops script.
        window.addEventListener('freeze', onDesktopFreeze, true);
        document.addEventListener('freeze', onDesktopFreeze, true);
      }
    });
  }

  isPhoneOrTablet(): boolean {
    const type = this.deviceDetector.getDeviceType();
    if (type === 2 || type === 3) {
      return true;
    }
    const ua = navigator.userAgent || '';
    if (/Android|iPhone|iPod|iPad|webOS|BlackBerry|IEMobile|Mobile/i.test(ua)) {
      return true;
    }
    const uaData = (navigator as any).userAgentData;
    if (uaData) {
      if (uaData.mobile === true) {
        return true;
      }
      const platform = String(uaData.platform || '');
      if (/Android|iOS|iPhone/i.test(platform)) {
        return true;
      }
    }
    const touchPoints = navigator.maxTouchPoints || 0;
    let coarse = false;
    try {
      coarse = window.matchMedia('(pointer: coarse)').matches;
    } catch { }
    const minSide = Math.min(screen.width || 0, screen.height || 0);
    // Chrome "Desktop site" spoofs desktop UA; touch + small screen stay phone-like
    if (touchPoints >= 2 && coarse && minSide > 0 && minSide <= 850) {
      return true;
    }
    if (touchPoints >= 2 && /arm|aarch64/i.test(navigator.platform || '')) {
      return true;
    }
    return false;
  }

  private phoneDropoutBound = false;

  private ensurePhoneDropoutListeners(): void {
    if (this.phoneDropoutBound || !this.isPhoneOrTablet()) {
      return;
    }
    this.bindPhoneDropoutListeners();
  }

  private bindPhoneDropoutListeners(): void {
    if (this.phoneDropoutBound) {
      return;
    }
    this.phoneDropoutBound = true;
    this.ngZone.runOutsideAngular(() => {
      const onPhoneBackground = () => this.markLeaveIfDropping();
      window.addEventListener('pagehide', onPhoneBackground, true);
      document.addEventListener('pagehide', onPhoneBackground as EventListener, true);
      window.addEventListener('freeze', onPhoneBackground, true);
      document.addEventListener('freeze', onPhoneBackground, true);
      window.addEventListener('unload', onPhoneBackground, true);
      // Recents often blurs while the page is still visible. Once hidden, the tab-switch send owns it.
      window.addEventListener('blur', () => {
        if (document.visibilityState === 'hidden' || document.hidden) {
          return;
        }
        onPhoneBackground();
      }, true);
    });
  }

  private markEngaged(): void {
    if (this.hasEngaged) {
      return;
    }
    this.hasEngaged = true;
    if (this.isLeavePromptActive()) {
      this.bindNativeUnload();
    }
  }

  private bindNativeUnload(): void {
    if (this.nativeUnloadBound) {
      return;
    }
    this.nativeUnloadBound = true;
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('beforeunload', this.nativeBeforeUnload, true);
      window.onbeforeunload = this.nativeBeforeUnload;
    });
  }

  private unbindNativeUnload(): void {
    if (!this.nativeUnloadBound) {
      return;
    }
    this.nativeUnloadBound = false;
    window.removeEventListener('beforeunload', this.nativeBeforeUnload, true);
    if (window.onbeforeunload === this.nativeBeforeUnload) {
      window.onbeforeunload = null;
    }
  }

  /** Pause abandon logging briefly (e.g. Terms PDF opens another tab). */
  pauseExitLogging(ms: number = 3000): void {
    this.suppressExitLogging = true;
    setTimeout(() => {
      this.suppressExitLogging = false;
    }, ms);
  }

  private shouldIgnoreBackgroundExit(): boolean {
    return this.isIntentionalExit || this.suppressExitLogging;
  }

  private markLeaveIfDropping(): void {
    if (this.shouldIgnoreBackgroundExit() || !this.isLeavePromptActive() || this.tabSwitchDropoutSent || this.dropoutCallbackSent) {
      return;
    }
    this.sendDropoutCallback();
    this.sendTempLog(SURVEY_TRACKING_MESSAGES.DROPOUT, true);
  }

  /** Beacon + keepalive fetch — iOS often kills one or the other on lock/app-switch. */
  private postDropoutKeepalive(url: string, body: string): void {
    const phone = this.isPhoneOrTablet();
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        if (phone) {
          navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        }
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      }
    } catch { }
    this.bumpDropoutCount();
    try {
      if (typeof fetch === 'function') {
        fetch(url, {
          method: 'POST',
          headers: { accept: 'application/json', 'Content-Type': 'application/json' },
          body,
          keepalive: true
        }).catch(() => { });
      }
    } catch { }
  }

  private bumpDropoutCount(): void {
    try {
      const total = Number(localStorage.getItem('dropoutApiCount') || 0) + 1;
      localStorage.setItem('dropoutApiCount', String(total));
      localStorage.removeItem('dropoutApiResponse');
      localStorage.removeItem('dropoutApiLast');
    } catch { }
  }

  private sendTempLog(message: string, isExitEvent: boolean): void {
    if (isExitEvent) {
      // Already terminal from a prior visit (or this session) → never re-send Dropout
      if (this.hasSentFinalEvent || this.isIntentionalExit || this.hasTerminalStatus()) {
        return;
      }
      this.hasSentFinalEvent = true;
      this.clearInactivityTimer();
    }

    const user = this.getUserParam();
    if (!user?.ip) {
      return;
    }

    try {
      const payload: userParam = {
        ...user,
        qparm: asQueryString(user.qparm, ''),
        devicetype: this.deviceDetector.getDeviceType(),
        policy: user.policy ?? 0
      };

      const tempDataUrl = this.rdService.getSurveyUserTempDataSaveUrl();
      const url = `${tempDataUrl}/${encodeURIComponent(message)}`;
      const body = JSON.stringify(payload);

      if (isExitEvent) {
        // Persist before fetch so a revisit mid-flight still skips Start
        this.setTerminalStatus('DROPOUT');
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true
        }).catch(err => console.warn('Exit temp-log failed', err));
      } else {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body
        }).catch(err => console.warn('Temp-log failed', err));
      }
    } catch (error) {
      console.error('Error saving survey user temp data:', error);
    }
  }

  private terminalStorageKey(): string | null {
    const user = this.getUserParam();
    if (!user?.utype || !user?.tforid) {
      return null;
    }
    // Include qparm so a new vendor param set starts a fresh landing/terminal cycle
    const qparm = asQueryString(user.qparm, '0');
    return `opinionest_final_${user.utype}_${user.tforid}_${qparm}`;
  }

  private getTerminalStatus(): TerminalKind | null {
    const key = this.terminalStorageKey();
    if (!key) {
      return null;
    }
    return localStorage.getItem(key) as TerminalKind | null;
  }

  private setTerminalStatus(kind: TerminalKind): void {
    const key = this.terminalStorageKey();
    if (!key) {
      return;
    }
    localStorage.setItem(key, kind);
    this.hasSentFinalEvent = true;
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    if (this.isIntentionalExit || this.hasSentFinalEvent || this.inactivityTracked || this.hasTerminalStatus()) {
      return;
    }

    this.inactivityTimer = setTimeout(() => {
      if (this.inactivityTracked || this.isIntentionalExit || this.hasSentFinalEvent || this.hasTerminalStatus()) {
        return;
      }
      this.inactivityTracked = true;
      this.track(SURVEY_TRACKING_MESSAGES.INACTIVE);
    }, this.INACTIVITY_TIMEOUT);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private resetDropoutInactivityTimer(): void {
    this.clearDropoutInactivityTimer();
    if (!this.isLeavePromptActive() || this.dropoutCallbackSent) {
      return;
    }
    this.inactivityDropoutTimer = setTimeout(() => {
      if (!this.isLeavePromptActive() || this.dropoutCallbackSent) {
        return;
      }
      this.sendDropoutCallback();
      this.track(SURVEY_TRACKING_MESSAGES.INACTIVE);
    }, this.INACTIVITY_TIMEOUT);
  }

  private clearDropoutInactivityTimer(): void {
    if (this.inactivityDropoutTimer) {
      clearTimeout(this.inactivityDropoutTimer);
      this.inactivityDropoutTimer = null;
    }
  }
}
